import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { patch, post, type Destination, type DestinationInput, type Profile } from '../api';
import { platformOf } from './adapter';
import { LogoSlot, PrimaryButton, SecondaryButton } from './ui';
import { Button, Chip, Icon } from '../ui';
import { StepFooter, StepHeading, linkBtn, type StepProps } from './SetupWizard';
import { destinationOf, domainOf, featured, profileSteps, serverFromPlatform, tileState } from './setup';
import { urlErrorFor } from './ProtocolSettings';
import { t } from '../i18n';

/** Put a destination returned by the API into the cached list (replace or append). */
function useStoreDestination() {
  const qc = useQueryClient();
  return (d: Destination) => {
    qc.setQueryData<Destination[]>(['destinations'], (old) =>
      old?.some((x) => x.id === d.id) ? old.map((x) => (x.id === d.id ? d : x)) : [...(old ?? []), d],
    );
    void qc.invalidateQueries({ queryKey: ['destinations'] });
  };
}

/** Step 2: featured platforms as tiles; add one, get its key, paste it. */
export function SetupStepDestinations({ profiles, dests, headingRef, go, focusProfile }: StepProps & { focusProfile?: string }) {
  const tiles = featured(profiles);
  const states = tiles.map((p) => tileState(destinationOf(p.id, dests)));
  const added = states.filter((s) => s !== 'idle').length;
  const keyed = states.filter((s) => s === 'keyed').length;

  return (
    <>
      <div className="flex flex-col gap-5 p-4 md:p-7">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between md:gap-4">
          <StepHeading headingRef={headingRef} sub="Pick the platforms you use. Each one goes live together with your stream.">
            Where do you stream?
          </StepHeading>
          <span className="text-[13px] text-muted">
            Others (Kick, Facebook, any RTMP) — later from <b className="text-text-soft">Add output</b>
          </span>
        </div>
        {!profiles ? (
          <p className="text-sm text-muted">Loading platforms…</p>
        ) : (
          // DS: VT-19 — four tiles (YouTube, Twitch, Instagram, TikTok) sit 2 × 2: three plus one alone read as a leftover
          <div className={`grid items-start gap-4 ${tiles.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {tiles.map((p) => (
              <Tile key={p.id} profile={p} dest={destinationOf(p.id, dests)} focus={focusProfile === p.id} />
            ))}
          </div>
        )}
      </div>

      <StepFooter
        left={
          <>
            <SecondaryButton className="max-md:min-h-[44px]" onClick={() => go(1)}>Back</SecondaryButton>
            <button type="button" data-testid="setup-skip" className={linkBtn} onClick={() => go(3)}>Skip for now</button>
          </>
        }
        hint={<span data-testid="setup-count">{added} added · {keyed} with a key</span>}
        primary={
          <PrimaryButton testId="setup-confirm" className="max-md:min-h-[44px]" onClick={() => go(3)}>
            Confirm and continue
          </PrimaryButton>
        }
      />
    </>
  );
}

function Tile({ profile: p, dest, focus }: { profile: Profile; dest?: Destination; focus: boolean }) {
  const store = useStoreDestination();
  const state = tileState(dest);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const opened = useRef(false);
  const [err, setErr] = useState('');
  const [how, setHow] = useState<HTMLElement | null>(null);
  const domain = domainOf(p.key_url);
  const closeHow = useCallback(() => setHow(null), []);
  // The tile changes its controls when it is added: the popover's button may be gone.
  useEffect(() => setHow(null), [state]);

  // One click: the key page opens right away (in the click, or the popup
  // blocker stops it) and the destination is created alongside. While the
  // request runs, further clicks do nothing; a retry after an error does not
  // open the page again.
  const add = () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setErr('');
    if (p.key_url && !opened.current) {
      opened.current = true;
      window.open(p.key_url, '_blank', 'noopener');
    }
    const body: DestinationInput = { name: p.name, profile: p.id, url: p.url, enabled: true, source: 'setup' };
    post<Destination>('/api/destinations', body)
      .then(store)
      .catch((e: Error) => setErr(e.message))
      .finally(() => {
        busyRef.current = false;
        setBusy(false);
      });
  };

  const steps = profileSteps(p);
  const goLive = p.go_live_note;
  // an added platform is a state (live edge: it goes live with the stream), not a highlight
  const frame = state === 'keyed' ? 'border-live/60' : state === 'added' ? 'border-dashed border-live/60' : 'border-line-2';

  return (
    <div data-testid={`setup-tile-${p.id}`} data-state={state} className={`relative flex min-w-0 flex-col gap-3 rounded-lg border bg-bg p-4 ${frame}`}>
      <div className="flex items-center gap-2.5">
        <LogoSlot platform={platformOf(p.id)} />
        <h2 className="m-0 text-base font-medium">{p.name}</h2>
        {state !== 'idle' && <Chip state="live" size="sm" className="ml-auto">ADDED</Chip>}
      </div>

      {serverFromPlatform(p) ? (
        // TikTok (VERT-PLAT): no key page and no default server — both are pasted in the tile. DS: VT-20
        <>
          {state === 'idle' && <span className="text-[13px] text-muted">{t('setup.tile.serverKey', { platform: p.name })}</span>}
          <ServerKeyForm profile={p} dest={dest} focus={focus} onSaved={store} />
          {!!steps?.length && (
            <button type="button" aria-expanded={!!how} onClick={(e) => setHow(how ? null : e.currentTarget)} className="min-h-[44px] self-center text-[13px] text-muted hover:text-text md:min-h-8">
              {t('setup.tile.how')}
            </button>
          )}
          {state !== 'idle' && goLive && <p data-testid="go-live-note" className="m-0 border-t border-line pt-2.5 text-xs text-text-soft">{goLive}</p>}
        </>
      ) : state === 'idle' ? (
        <>
          <span className="text-[13px] text-muted">We add {p.name} to your destinations and open the page with your stream key.</span>
          <Button busy={busy} onClick={add} className="w-full max-md:min-h-[44px]">
            {!busy && <Icon name={err ? 'refresh' : 'plus'} size={16} />}
            {err ? `Try again: add ${p.name}` : `Add ${p.name} & get key`}
            {!err && p.key_url && <Icon name="external" size={12} />}
          </Button>
          {err && <span role="alert" className="flex items-center gap-1 text-xs text-fail"><Icon name="state-fail" size={12} />{err}</span>}
          {!!steps?.length && (
            <button type="button" aria-expanded={!!how} onClick={(e) => setHow(how ? null : e.currentTarget)} className="min-h-[44px] self-center text-[13px] text-muted hover:text-text md:min-h-8">
              How do I get the key?
            </button>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[13px]">
            {p.key_url && (
              <a href={p.key_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-1 text-accent no-underline hover:underline md:min-h-0">
                Get key<Icon name="external" size={12} /><span className="sr-only"> (opens {domain})</span>
              </a>
            )}
            {p.key_url && !!p.key_steps?.length && <span aria-hidden className="text-faint">·</span>}
            {!!p.key_steps?.length && (
              <button type="button" aria-expanded={!!how} aria-label={`How do I get the ${p.name} key?`} onClick={(e) => setHow(how ? null : e.currentTarget)}
                className={`min-h-[44px] md:min-h-0 ${how ? 'text-accent' : 'text-muted hover:text-text'}`}>
                How?
              </button>
            )}
          </div>
          <KeyField profile={p} dest={dest!} focus={focus} onSaved={store} />
          {goLive && <p data-testid="go-live-note" className="m-0 border-t border-line pt-2.5 text-xs text-text-soft">{goLive}</p>}
        </>
      )}

      {how && <HowPopover profile={p} domain={domain} trigger={how} onClose={closeHow} />}
    </div>
  );
}

/** Stream key of an added tile. Empty input sends nothing ("" would remove the key). */
function KeyField({ profile: p, dest, focus, onSaved }: { profile: Profile; dest: Destination; focus: boolean; onSaved: (d: Destination) => void }) {
  const [key, setKey] = useState('');
  const [replace, setReplace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const keyed = !dest.needs_key;
  const editing = !keyed || replace;

  useEffect(() => {
    if (focus && editing) input.current?.focus();
  }, [focus, editing]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const v = key.trim();
    if (!v || saving) return;
    if (/\s/.test(v)) return setErr('The key has no spaces. Copy it again.');
    if (v.length < 8) return setErr('That looks too short for a stream key.');
    setSaving(true);
    setErr('');
    try {
      const d = await patch<Destination>(`/api/destinations/${dest.id}`, { key: v });
      setKey(''); // never kept after it is sent
      setReplace(false);
      onSaved(d);
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span role="status" className="flex items-center gap-1.5 text-[13px] text-live"><Icon name="check" size={12} />Key saved<span className="text-muted"> · Goes live when you stream.</span></span>
        <button type="button" onClick={() => setReplace(true)} className="min-h-[44px] text-xs text-muted hover:text-text md:min-h-8">Replace key</button>
      </div>
    );
  }

  const id = `setup-key-${p.id}`;
  return (
    <form onSubmit={save} className="flex flex-col gap-1.5" aria-label={`${p.name} stream key`}>
      <label htmlFor={id} className="text-xs text-muted">Stream key</label>
      <span className={`flex min-h-[44px] items-center gap-1.5 rounded-md border bg-bg pr-1 pl-2.5 md:min-h-[38px] ${err ? 'border-fail' : 'border-line-strong focus-within:border-accent focus-within:shadow-focus'}`}>
        <input ref={input} id={id} type="password" autoComplete="off" spellCheck={false} value={key} disabled={saving}
          placeholder={`Paste your ${p.name} key`} aria-invalid={!!err}
          onChange={(e) => { setKey(e.target.value); setErr(''); }}
          className="min-w-0 flex-1 bg-transparent font-mono text-xs text-text outline-none placeholder:text-muted" />
        <Button type="submit" size="sm" busy={saving} disabled={!key.trim() || saving} className="max-md:min-h-9 md:!h-[30px]">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </span>
      {err ? (
        <span role="alert" className="flex items-center gap-1 text-[11px] text-fail"><Icon name="state-fail" size={12} />{err}</span>
      ) : (
        <span className="text-[11px] text-muted">{p.key_per_session ? 'New key for every live.' : 'Or paste it later in its card.'}</span>
      )}
      {replace && <button type="button" onClick={() => { setReplace(false); setKey(''); setErr(''); }} className="self-start text-xs text-muted hover:text-text">Keep the saved key</button>}
    </form>
  );
}

/**
 * Server and stream key of a platform that shows both (TikTok, VERT-PLAT): one form adds the
 * output (source=setup) or replaces both on an added one. The key is never kept after it is
 * sent; the server field is the user's own address and stays (it is not a secret by itself).
 */
function ServerKeyForm({ profile: p, dest, focus, onSaved }: { profile: Profile; dest?: Destination; focus: boolean; onSaved: (d: Destination) => void }) {
  // the saved address comes back as the user typed it, unless it carried the key (then masked: not reused)
  const savedURL = dest?.url && !dest.url.includes('****') ? dest.url : '';
  const [url, setUrl] = useState(savedURL);
  const [key, setKey] = useState('');
  const [replace, setReplace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const keyed = !!dest && !dest.needs_key;
  const editing = !keyed || replace;

  useEffect(() => {
    if (focus && editing) input.current?.focus();
  }, [focus, editing]);

  const urlError = url.trim() ? urlErrorFor(url, 'rtmps') : '';
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const u = url.trim();
    const v = key.trim();
    if (saving) return;
    if (!u) return setErr(t('add.serverNeeded', { platform: p.name }));
    if (urlError) return setErr(urlError);
    if (/\s/.test(v)) return setErr('The key has no spaces. Copy it again.');
    if (v && v.length < 8) return setErr('That looks too short for a stream key.');
    setSaving(true);
    setErr('');
    try {
      const body: DestinationInput = {};
      if (!dest || u !== savedURL) body.url = u;
      if (v) body.key = v;
      const d = dest
        ? await patch<Destination>(`/api/destinations/${dest.id}`, body)
        : await post<Destination>('/api/destinations', { ...body, name: p.name, profile: p.id, enabled: true, source: 'setup' });
      setKey(''); // never kept after it is sent
      setReplace(false);
      onSaved(d);
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span role="status" className="flex items-center gap-1.5 text-[13px] text-live"><Icon name="check" size={12} />{t('setup.tile.saved')}</span>
        <button type="button" data-testid={`setup-replace-${p.id}`} onClick={() => setReplace(true)} className="min-h-[44px] text-xs text-muted hover:text-text md:min-h-8">{t('setup.tile.replace')}</button>
      </div>
    );
  }

  const id = `setup-${p.id}`;
  const box = (bad: boolean) => `flex min-h-[44px] items-center rounded-md border bg-bg px-2.5 md:min-h-[38px] ${bad ? 'border-fail' : 'border-line-strong focus-within:border-accent focus-within:shadow-focus'}`;
  return (
    <form onSubmit={save} data-testid={`setup-form-${p.id}`} className="flex flex-col gap-1.5" aria-label={t('add.formLabel', { platform: p.name })}>
      <label htmlFor={`${id}-url`} className="text-xs text-muted">{t('add.server')}</label>
      <span className={box(!!urlError)}>
        <input ref={input} id={`${id}-url`} name="url" type="text" autoComplete="off" spellCheck={false} value={url} disabled={saving}
          placeholder="rtmp:// or rtmps://" aria-invalid={!!urlError}
          onChange={(e) => { setUrl(e.target.value); setErr(''); }}
          className="min-w-0 flex-1 bg-transparent font-mono text-xs text-text outline-none placeholder:text-muted" />
      </span>
      <span className="text-[11px] text-muted">{t('add.serverFromPlatform', { platform: p.name })}</span>
      <label htmlFor={`${id}-key`} className="mt-1 text-xs text-muted">{t('add.key')}</label>
      <span className={`${box(false)} gap-1.5 pr-1`}>
        <input id={`${id}-key`} name="key" type="password" autoComplete="off" spellCheck={false} value={key} disabled={saving}
          placeholder={t('fix.key.placeholder', { platform: p.name })}
          onChange={(e) => { setKey(e.target.value); setErr(''); }}
          className="min-w-0 flex-1 bg-transparent font-mono text-xs text-text outline-none placeholder:text-muted" />
      </span>
      {err && <span role="alert" className="flex items-center gap-1 text-[11px] text-fail"><Icon name="state-fail" size={12} />{err}</span>}
      <Button type="submit" busy={saving} disabled={!url.trim() || saving} className="mt-1 w-full max-md:min-h-[44px]">
        {!saving && !dest && <Icon name="plus" size={16} />}
        {saving ? t('setup.tile.saving') : dest ? t('setup.tile.save') : t('setup.tile.add', { platform: p.name })}
      </Button>
      {replace && <button type="button" onClick={() => { setReplace(false); setKey(''); setUrl(savedURL); setErr(''); }} className="self-start text-xs text-muted hover:text-text">{t('setup.tile.keep')}</button>}
    </form>
  );
}

/**
 * How to get the key: steps of the profile, under the tile's controls (in the
 * flow, so it never covers the footer). Closes with Escape, Close or a click
 * outside; focus goes back to the button.
 */
function HowPopover({ profile: p, domain, trigger, onClose }: { profile: Profile; domain: string; trigger: HTMLElement; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), []);
  useEffect(() => {
    const close = (refocus: boolean) => {
      onClose();
      if (refocus) trigger.focus();
    };
    const down = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (ref.current?.contains(t) || trigger.contains(t)) return;
      close(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
    };
  }, [onClose, trigger]);

  return (
    <div ref={ref} tabIndex={-1} role="dialog" aria-label={`How to get your ${p.name} key`}
      className="flex animate-condense flex-col gap-2.5 rounded-lg border border-line-2 bg-panel p-3.5 shadow-pop outline-none">
      <span className="font-medium">Get your {p.name} key</span>
      <ol className="m-0 flex list-none flex-col gap-2 p-0 text-[13px] text-text-soft">
        {profileSteps(p)?.map((s, i) => (
          <li key={i} className="flex gap-2.5">
            <span className="font-mono text-xs font-medium text-muted">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      {p.go_live_note && <p data-testid="go-live-note" className="m-0 rounded-md border border-line px-2.5 py-2 text-xs text-text-soft">{p.go_live_note}</p>}
      <div className="flex items-center justify-between gap-2">
        {p.key_url ? (
          <a href={p.key_url} target="_blank" rel="noopener noreferrer"
            className="sx-btn sm inline-flex no-underline max-md:min-h-[44px]">
            Open {domain || p.name}<Icon name="external" size={12} />
          </a>
        ) : <span />}
        <button type="button" onClick={() => { onClose(); trigger.focus(); }} className="min-h-[44px] text-[13px] text-muted hover:text-text md:min-h-8">Close</button>
      </div>
    </div>
  );
}
